import axios from 'axios';
import { clear } from 'hooks/context/clear';
import { convertEmptyStringsToNull } from 'utils/objectsUtils';
import { v4 as uuid } from 'uuid';
import queryString from 'query-string';

const TRIM_SKIP_KEYS = ['sql', 'bqCreate'];

export const deepTrim = (obj: unknown, skipKeys: string[] = TRIM_SKIP_KEYS): unknown => {
  if (typeof obj === 'string') {
    const trimmed = obj.trim();
    return trimmed.length > 0 ? trimmed : obj;
  }
  if (Array.isArray(obj)) return obj.map((item) => deepTrim(item, skipKeys));
  if (obj instanceof Date) return obj;
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, skipKeys.includes(k) ? v : deepTrim(v, skipKeys)])
    );
  }
  return obj;
};

export const trimFormData = (formData: FormData): FormData => {
  const trimmed = new FormData();
  formData.forEach((value, key) => {
    if (typeof value === 'string' && !TRIM_SKIP_KEYS.includes(key)) {
      const trimmedValue = value.trim();
      trimmed.append(key, trimmedValue.length > 0 ? trimmedValue : value);
    } else {
      trimmed.append(key, value);
    }
  });
  return trimmed;
};

interface CreateHttpClientOptions {
  convertEmptyStrings: boolean;
}

export const createHttpClient = (baseURL: string, options: CreateHttpClientOptions) => {
  const { convertEmptyStrings } = options;

  const instance = axios.create({
    baseURL,
    paramsSerializer: (params) =>
      queryString.stringify(params, {
        arrayFormat: 'comma',
        skipNull: true,
        skipEmptyString: true,
      }),
  });

  instance.interceptors.request.use(
    (config) => {
      const conf = {
        ...config,
        headers: axios.AxiosHeaders.from({
          ...config.headers,
          authorization: localStorage.getItem('bearer'),
          'X-Correlation-Id': uuid(),
        }),
      };

      if (config.method === 'post' || config.method === 'put') {
        if (config.data instanceof FormData) {
          conf.data = trimFormData(config.data);
        } else {
          const trimmed = deepTrim(config.data) as object;
          conf.data = convertEmptyStrings ? convertEmptyStringsToNull(trimmed) : trimmed;
        }
      }

      if (config.params) {
        conf.params = deepTrim(config.params);
      }

      return conf;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        clear();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};
